import React, { memo, useEffect, useMemo, useState } from "react";
import CommonFormFieldWrapper, { getFormInputErrors } from "../../../../../common/components/form-field-wrapper/CommonFormFieldWrapper";
import { Controller, useForm } from "react-hook-form";
import ProductFormWrapper from "../components/ProductFormWrapper";
import { FirstStepOfSolarProductDetailsState, submitFirstStep } from "../../../../../stores/slices/solar-product-details-slice";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../../stores/store";
import { NumericFormat } from "react-number-format";
import styles from "./FirstStepOfProductDetails.module.scss";
import CommonSelect, { CommonSelectItem } from "../../../../../common/components/select/CommonSelect";
import { ProductCategory } from "../../../../../model/appliance";
import { BounceLoader } from "react-spinners";

type Props = {
	categories: Array<ProductCategory> | [];
	fetchingCategories: boolean;
};

const FirstStepOfProductDetails: React.FC<Props> = memo((props: Props) => {
	const dispatch = useDispatch();
	const firstStepData = useSelector((state: RootState) => state.solarProductDetails.firstStep);
	const [categoryIds, setCategoryIds] = useState<string[]>(firstStepData?.categoryIds ?? []);
	const {
		register,
		handleSubmit,
		control,
		setValue,
		formState: { errors, isValid, isSubmitted, touchedFields },
	} = useForm<FirstStepOfSolarProductDetailsState>({
		defaultValues: firstStepData,
		reValidateMode: "onChange",
		mode: "onTouched",
	});

	useEffect(() => {
		setValue("categoryIds", categoryIds, { shouldValidate: isSubmitted });
	}, [categoryIds, isSubmitted, touchedFields.categoryIds, setValue]);

	const Categories = useMemo(() => {
		return props?.categories?.map(item => new CommonSelectItem(item.id, item.name)) || [];
	}, [props?.categories]);

	const OnCategorySelected = (value: CommonSelectItem | undefined) => {
		setValue("categoryIds", [value?.key]);
		if (value) {
			setCategoryIds(prev => {
				if (prev.includes(value.key)) {
					return prev.filter(id => id !== value.key);
				} else {
					return [...prev, value.key];
				}
			});
		}
	};

	const removeCategory = (id: string) => {
		setCategoryIds(prev => prev.filter(categoryId => categoryId !== id));
	};

	const submit = (data: any) => {
		data.categoryIds = categoryIds;
		dispatch(submitFirstStep(data));
	};

	useEffect(() => {
		const fieldsToSet = ["productName", "productCapacity", "price", "activeDuration", "packageDescription", "batteryType"];

		fieldsToSet.forEach(field => {
			if (!firstStepData[field as keyof FirstStepOfSolarProductDetailsState]) {
				setValue(field as keyof FirstStepOfSolarProductDetailsState, firstStepData[field as keyof FirstStepOfSolarProductDetailsState]);
				return;
			}

			setValue(field as keyof FirstStepOfSolarProductDetailsState, firstStepData[field as keyof FirstStepOfSolarProductDetailsState], {
				shouldValidate: true,
			});
		});
	}, [firstStepData, setValue]);

	return (
		<ProductFormWrapper isValid={isValid} isSubmitted={isSubmitted} title="Product Details" onNext={handleSubmit(submit)}>
			<CommonFormFieldWrapper title="Product Name" required={true} errors={getFormInputErrors(errors.productName?.message)}>
				<input
					{...register("productName", {
						required: "Product name is required",
					})}
					placeholder="Enter Product Name"
				/>
			</CommonFormFieldWrapper>
			<span className="spacer" />

			{props.fetchingCategories ? (
				<div style={{ display: "flex", alignItems: "center", gap: "15px", marginBottom: "20px" }}>
					{" "}
					<BounceLoader size={34} /> Fetching categories...
				</div>
			) : (
				<>
					<CommonFormFieldWrapper title="Product Category" required errors={getFormInputErrors(errors.categoryIds?.message)}>
						<CommonSelect
							{...register("categoryIds", {
								required: "Product category is required",
							})}
							onChange={OnCategorySelected}
							items={Categories}
							placeholder="Select category"
							searchable
						/>
					</CommonFormFieldWrapper>
					{categoryIds?.length > 0 && (
						<div className={styles.pillsContainer}>
							{categoryIds.map((id, index) => (
								<div key={index} className={styles.pill}>
									<span className={styles.pillText}>{Categories.find(item => item.key === id)?.label || id}</span>
									<button className={styles.pillCloseButton} onClick={() => removeCategory(id)}>
										&times;
									</button>
								</div>
							))}
						</div>
					)}
				</>
			)}
			<span className="spacer" />

			<CommonFormFieldWrapper title="Product Capacity" required={true} errors={getFormInputErrors(errors.productCapacity?.message)}>
				<input
					type="number"
					min={0}
					{...register("productCapacity", {
						required: "Product capacity is required",
						valueAsNumber: true,
						validate: value => {
							if (value <= 0) {
								return "Product capacity cannot be less than or equal to 0";
							}
							return true;
						},
					})}
					placeholder="Enter Product Capacity"
				/>
			</CommonFormFieldWrapper>
			<span className="spacer" />
			<CommonFormFieldWrapper title="Priced At" required={true} errors={getFormInputErrors(errors.price?.message)}>
				<Controller
					render={({ field }) => (
						<div className={styles.priceInput}>
							<div className={styles.currency}>₦</div>
							<NumericFormat
								placeholder="Enter Price"
								allowNegative={false}
								allowLeadingZeros={false}
								thousandSeparator={true}
								onValueChange={values => {
									field.onChange(values.value);
								}}
								onBlur={field.onBlur}
								value={field.value}
							/>
						</div>
					)}
					name="price"
					control={control}
					rules={{
						required: "Price is required",
						validate: value => {
							if (Number.parseFloat(value) <= 0) {
								return "Price cannot be less than or equal to 0";
							}
							return true;
						},
					}}
				/>
			</CommonFormFieldWrapper>
			<span className="spacer" />
			<CommonFormFieldWrapper title="Active Duration" errors={getFormInputErrors(errors.activeDuration?.message)}>
				<input
					type="number"
					{...register("activeDuration", {
						valueAsNumber: true,
						min: {
							value: 0,
							message: "Active Duration cannot be less than 0",
						},
					})}
					placeholder="How many hours it can last for"
				/>
			</CommonFormFieldWrapper>
			<span className="spacer" />
			<CommonFormFieldWrapper title="Package Description" required errors={getFormInputErrors(errors.packageDescription?.message)}>
				<input {...register("packageDescription",{
								required: "Description is required",
							})} placeholder="Enter Package Description" />
			</CommonFormFieldWrapper>
			<span className="spacer" />
			<div className="formFieldInlineContainer">
				<CommonFormFieldWrapper title="Battery Type/Name">
					<input {...register("batteryType")} placeholder="Enter Battery Type/Name" />
				</CommonFormFieldWrapper>
			</div>
		</ProductFormWrapper>
	);
});

export default FirstStepOfProductDetails;
